import { ArrowRight } from 'lucide-react';
import type { BookingResponse, GuestProfileResponse, InvoiceResponse } from '@/shared/api';
import { formatBookingPrice } from '@/shared/lib/bookingPrice';
import { formatMoney } from '@/shared/lib/format';
import { BookedServicesCard } from './BookedServicesCard';
import { BookingContactSummary } from './BookingContactSummary';
import type { BookingDetailTab } from './bookingDetailTabModel';
import { summarizeInvoiceGroups, type InvoiceStepState } from './bookingInvoiceModel';
import { CleaningPreparationCard } from './CleaningPreparationCard';
import { overviewTodos } from './overviewTodos';
import { OverviewOnboardingCard, unknownSources } from './OverviewOnboardingCard';
import { OverviewTodoList } from './OverviewTodoList';
import { SectionCard } from './SectionCard';
import type { GuestState } from './useBookingWorkspace';

interface Props {
  booking: BookingResponse;
  guest: GuestProfileResponse | undefined;
  guestState: GuestState;
  guestName: string;
  invoices: InvoiceResponse[] | null;
  invoicesFailed: boolean;
  onboardingComplete: boolean | null;
  invoiceState: InvoiceStepState | null;
  onChangeTab: (tab: BookingDetailTab) => void;
}

/** Reiter Überblick (Board „Buchung“): zu erledigen, Aufenthalt und Kontakt, Onboarding, Reinigung, Geld, Notizen. */
export function BookingOverview({
  booking,
  guest,
  guestState,
  guestName,
  invoices,
  invoicesFailed,
  onboardingComplete,
  invoiceState,
  onChangeTab,
}: Props) {
  const todos = overviewTodos(booking, onboardingComplete, invoiceState);
  const summary = invoices ? summarizeInvoiceGroups(invoices, booking.currency) : null;
  const moneyLine =
    !invoices || !summary
      ? invoicesFailed
        ? 'Rechnungen nicht geladen – unter Geld erneut versuchen.'
        : 'Rechnungen werden geladen …'
      : invoices.length === 0
        ? 'Noch keine Rechnung erzeugt.'
        : summary.currencyMismatch
          ? 'Rechnungssumme nicht verfügbar – abweichende Währung.'
          : `Rechnungen ${formatMoney(summary.activeTotal, booking.currency)} · davon offen ${formatMoney(summary.openTotal, booking.currency)}`;
  return (
    <div className="flex flex-col gap-4">
      <OverviewTodoList
        todos={todos}
        unknown={unknownSources(guestState, onboardingComplete, invoiceState, invoicesFailed)}
        onChangeTab={onChangeTab}
      />
      <div className="@container">
        <div className="@2xl:grid-cols-2 grid gap-4">
          <SectionCard icon="Bed" title="Aufenthalt & Kontakt">
            <BookingContactSummary
              guestName={guestName}
              phone={guest?.phone ?? booking.guestPhone}
              email={guest?.email}
            />
            <TabLink label="Gast-Daten ansehen" onClick={() => onChangeTab('guest')} />
          </SectionCard>
          <OverviewOnboardingCard
            guestState={guestState}
            onboardingComplete={onboardingComplete}
            onOpen={() => onChangeTab('guest')}
          />
          <CleaningPreparationCard preparation={booking} />
          <BookedServicesCard booking={booking} />
          <SectionCard icon="Database" title="Geld">
            <p className="font-serif text-2xl font-bold">{formatBookingPrice(booking)}</p>
            <p className="text-ink-soft text-sm">{moneyLine}</p>
            <TabLink label="Zu Geld" onClick={() => onChangeTab('billing')} />
          </SectionCard>
          {booking.notes && (
            <SectionCard
              icon="Edit"
              title="Interne Notizen"
              className="bg-card-alt @2xl:col-span-2"
            >
              <p className="text-ink-soft whitespace-pre-wrap text-sm leading-relaxed">
                {booking.notes}
              </p>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}

function TabLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-forest mt-auto inline-flex min-h-11 items-center gap-1.5 self-start text-sm font-bold"
    >
      {label} <ArrowRight size={15} aria-hidden />
    </button>
  );
}
